import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

// Lightweight toast notifications — no external dependency.
//   const toast = useToast();
//   toast.success('Saved'); toast.error('Upload failed'); toast.info('…');
const ToastContext = createContext(null);

const STYLES = {
  success: { Icon: CheckCircle, cls: 'border-green-500/40 text-green-300' },
  error:   { Icon: AlertCircle, cls: 'border-red-500/40 text-red-300' },
  info:    { Icon: Info,        cls: 'border-champagne/40 text-champagne' },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const remove = useCallback((id) => setToasts(ts => ts.filter(t => t.id !== id)), []);

  const push = useCallback((message, type = 'info', ttl = 4000) => {
    if (!message) return;
    const id = ++idRef.current;
    setToasts(ts => [...ts, { id, message: String(message), type }]);
    if (ttl) setTimeout(() => remove(id), ttl);
  }, [remove]);

  const api = useRef({
    success: (m, ttl) => push(m, 'success', ttl),
    error: (m, ttl) => push(m, 'error', ttl),
    info: (m, ttl) => push(m, 'info', ttl),
  }).current;

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-[calc(100%-2rem)] sm:w-auto" role="status" aria-live="polite">
        {toasts.map(t => {
          const { Icon, cls } = STYLES[t.type] || STYLES.info;
          return (
            <div key={t.id} className={`flex items-start gap-2 bg-midnight border ${cls} rounded-lg shadow-lg px-4 py-3 text-sm`}>
              <Icon className="w-4 h-4 mt-0.5 shrink-0" />
              <span className="flex-1 text-text-light">{t.message}</span>
              <button onClick={() => remove(t.id)} aria-label="Dismiss" className="text-text-muted hover:text-text-light shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

// Safe no-op fallback if a component renders outside the provider (e.g. tests).
const NOOP = { success: () => {}, error: () => {}, info: () => {} };
export function useToast() {
  return useContext(ToastContext) || NOOP;
}

export default ToastProvider;
